/* eslint-disable @typescript-eslint/no-unused-vars */
import { Checkbox, Tag } from 'antd';
import { CommonSelect } from '../../../../../components/select';
import getFormatMessage from '../../../../../locales/get-format-message';
import {
  IAPSdkVersionOption,
  IGCloudSdkItem,
} from '../../../../../model/api-modules';
import styles from './index.module.scss';
import { RightOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { CustomPopover } from '../../../../../components/custom-popover';
import classNames from 'classnames';
import { compareVersions } from '../../../../../lib';

interface IProps {
  name?: string;
  sdkInfo: IAPSdkVersionOption[];
  value?: IGCloudSdkItem[];
  onChange?: (values: IGCloudSdkItem[]) => void;
}

export const SdkSelect = (props: IProps) => {
  const { sdkInfo, onChange, value } = props;
  const [selectedSdk, setSelectedSdk] = useState<IAPSdkVersionOption>(
    sdkInfo[0]
  );
  const [selectedVersions, setSelectedVersions] = useState<IGCloudSdkItem[]>(
    value || []
  );

  useEffect(() => {
    setSelectedVersions(value || []);
  }, [value]);

  const onValuesChange = (newValues: IGCloudSdkItem[]) => {
    onChange?.(newValues);
    setSelectedVersions(newValues);
  };

  const dropdownRender = () => {
    const onChange = (ev: CheckboxChangeEvent, version: IGCloudSdkItem) => {
      const { checked } = ev.target;
      if (checked) {
        onValuesChange([...selectedVersions, version]);
        return;
      }
      onValuesChange(
        selectedVersions.filter(
          (item) =>
            item.sdkVersion !== version.sdkVersion ||
            item.sdkName !== version.sdkName
        )
      );
    };

    const checkAll = (operateSdk: IAPSdkVersionOption, checked: boolean) => {
      const currentALlVersions = operateSdk.sdkVersionList.map((item) => ({
        sdkVersion: item.sdkVersion,
        sdkName: operateSdk.sdkName,
      }));
      if (checked) {
        onValuesChange([...selectedVersions, ...currentALlVersions]);
        return;
      }
      onValuesChange(
        selectedVersions.filter(
          (selectedVersion) =>
            !currentALlVersions.some(
              (item) =>
                item.sdkName === selectedVersion.sdkName &&
                item.sdkVersion === selectedVersion.sdkVersion
            )
        )
      );
    };

    const isSelectSdkAll = (operateSdk: IAPSdkVersionOption) => {
      const currentAllVersions = operateSdk.sdkVersionList.map((item) => ({
        sdkVersion: item.sdkVersion,
        sdkName: operateSdk.sdkName,
      }));
      const isAll = currentAllVersions.every((item) =>
        selectedVersions.some(
          (subItem) =>
            subItem.sdkName === item.sdkName &&
            subItem.sdkVersion === item.sdkVersion
        )
      );
      return isAll;
    };

    const isSdkIndeterminate = (operateSdk: IAPSdkVersionOption) => {
      const selectSdkAllVersions = operateSdk.sdkVersionList.map((item) => ({
        sdkVersion: item.sdkVersion,
        sdkName: operateSdk.sdkName,
      }));
      return (
        !isSelectSdkAll(operateSdk) &&
        selectSdkAllVersions.some((item) =>
          selectedVersions.some(
            (subItem) =>
              subItem.sdkName === item.sdkName &&
              subItem.sdkVersion === item.sdkVersion
          )
        )
      );
    };

    return (
      <div className={styles.sdkSelectMenu}>
        <div className={styles.sdkSelectLeft}>
          {[...sdkInfo]
            .sort((a, b) => (a.sdkName > b.sdkName ? 1 : -1))
            .map((item) => {
              return (
                <div
                  className={classNames(styles.sdkLabel, {
                    [styles.selected]: selectedSdk === item,
                  })}
                  onClick={() => setSelectedSdk(item)}
                >
                  <Checkbox
                    onChange={(ev) => checkAll(item, ev.target.checked)}
                    checked={isSelectSdkAll(item)}
                    indeterminate={isSdkIndeterminate(item)}
                  />
                  <span className="ellipsis">{item.sdkName}</span>
                  <RightOutlined />
                </div>
              );
            })}
        </div>

        <div className={styles.sdkSelectRight}>
          <div>
            <span className={styles.sdkSelectAllInfo}>{`${getFormatMessage(
              '组件版本（对应上架的GCloud版本）'
            )}`}</span>
          </div>
          {[...selectedSdk.sdkVersionList]
            .sort((a, b) => compareVersions(a.sdkVersion, b.sdkVersion))
            .map((sdkVersion) => {
              return (
                <Checkbox
                  checked={selectedVersions.some(
                    (item) =>
                      item.sdkName === selectedSdk.sdkName &&
                      item.sdkVersion === sdkVersion.sdkVersion
                  )}
                  onChange={(ev) =>
                    onChange(ev, {
                      sdkName: selectedSdk.sdkName,
                      sdkVersion: sdkVersion.sdkVersion,
                    })
                  }
                >{`${sdkVersion.sdkVersion}(${sdkVersion.gcloudVersionList.join(
                  '/'
                )})`}</Checkbox>
              );
            })}
        </div>
      </div>
    );
  };

  const values = selectedVersions.map(
    (item) => `${item.sdkName}/${item.sdkVersion}`
  );

  const maxTagRender = () => {
    const getAllValues = () => {
      return (
        <div>
          {values.map((value) => {
            return <div>{value}</div>;
          })}
        </div>
      );
    };
    return (
      <CustomPopover
        content={getAllValues()}
        overlayClassName={styles.allSdkValues}
      >
        <span>{`+${selectedVersions.length - 3}`}</span>
      </CustomPopover>
    );
  };

  return (
    <CommonSelect
      value={values}
      className={styles.sdkSelector}
      placeholder={getFormatMessage('select')}
      dropdownRender={dropdownRender}
      getPopupContainer={(el) => el.parentNode}
      showSearch={false}
      mode="multiple"
      popupClassName={styles.sdkSelectPopup}
      maxTagCount={3}
      allowClear
      dropdownMatchSelectWidth={false}
      maxTagPlaceholder={maxTagRender()}
      onChange={(values) => {
        const newValues = values.map((value = '') => {
          const [sdkName, sdkVersion] = value.split('/');
          return { sdkName, sdkVersion };
        });
        setSelectedVersions(newValues);
        onChange?.(newValues);
      }}
    />
  );
};
